import { Component, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, finalize, combineLatest } from 'rxjs';

import { TargetAudienceLocation } from '../models/target-audience-location';
import { TargetAreaSummary } from '../models/target-area-summary';
import {
  TargetCompany,
  TargetCompanyEntry
} from '../models/target-company';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

@Component({
  selector: 'app-target-area-list',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './target-area-list.component.html',
  styleUrl: './target-area-list.component.css'
})
export class TargetAreaListComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly collectDataService = inject(CollectDataService);
  private readonly uiState = inject(UiStateService);

  private subscription: Subscription | null = null;

  protected readonly serviceId = signal<number | null>(null);
  protected readonly locations = signal<TargetAudienceLocation[]>([]);
  protected readonly loading = signal<boolean>(true);
  protected readonly statusMessage = signal<string>('');
  protected readonly errorMessage = signal<string>('');
  protected readonly filteredAudienceId = signal<number | null>(null);
  protected readonly companyPending = signal<number | null>(null);
  protected readonly companyStatus = signal<string>('');
  protected readonly companyError = signal<string>('');
  protected readonly activeLocationId = signal<number | null>(null);
  protected readonly companyResults = signal<Record<number, TargetCompanyEntry[]>>(
    {}
  );

  constructor() {
    this.subscription = combineLatest([
      this.route.paramMap,
      this.route.queryParamMap
    ]).subscribe(([params, query]) => {
      const serviceParam = params.get('serviceId');
      const parsedServiceId = serviceParam ? Number(serviceParam) : NaN;

      if (Number.isNaN(parsedServiceId)) {
        this.errorMessage.set('Invalid service identifier.');
        this.loading.set(false);
        return;
      }

      const audienceParam = query.get('audienceId');
      const parsedAudienceId = audienceParam ? Number(audienceParam) : NaN;
      this.filteredAudienceId.set(
        Number.isNaN(parsedAudienceId) ? null : parsedAudienceId
      );

      this.serviceId.set(parsedServiceId);
      this.uiState.setServiceId(parsedServiceId);
      this.loadLocations(parsedServiceId);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  protected viewAudience(location: TargetAudienceLocation): void {
    const serviceId = this.serviceId();
    if (!serviceId) {
      return;
    }
    this.uiState.setServiceId(serviceId);
    this.uiState.setAudienceId(location.target_audience_id);
    this.uiState.setLocationId(location.id);
    this.router.navigate(['/audiences', serviceId, location.target_audience_id]);
  }

  protected viewCompanies(location: TargetAudienceLocation): void {
    const serviceId = this.serviceId();
    if (!serviceId) {
      return;
    }
    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(location.id);
    this.uiState.setAudienceId(location.target_audience_id);
    this.router.navigate(['/companies', serviceId, location.id]);
  }

  protected generateCompanies(location: TargetAudienceLocation): void {
    const serviceId = this.serviceId();
    if (!serviceId) {
      this.companyError.set('Missing service identifier for generation.');
      return;
    }

    this.companyError.set('');
    this.companyStatus.set('Generating target companies...');
    this.companyPending.set(location.id);
    this.activeLocationId.set(location.id);

    this.collectDataService
      .generateTargetCompany(serviceId, location.id)
      .pipe(
        finalize(() => {
          this.companyPending.set(null);
        })
      )
      .subscribe({
        next: (result) => {
          const isSuccess =
            result?.status?.toLowerCase() === 'success' ||
            result?.success === true;
          if (!isSuccess) {
            const message =
              result?.message ||
              'Target company generation did not complete. Please try again.';
            this.companyError.set(message);
            this.companyStatus.set('');
            return;
          }

          this.companyStatus.set(
            result?.message ||
              `Target companies generated successfully for location ${location.id}.`
          );
          this.loadCompanies(serviceId, location.id);
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to generate target companies. Please try again.';
          this.companyError.set(message);
          this.companyStatus.set('');
        }
      });
  }

  protected clearFilter(): void {
    const serviceId = this.serviceId();
    if (!serviceId) {
      return;
    }
    this.filteredAudienceId.set(null);
    void this.router.navigate(['/target-areas', serviceId]);
  }

  protected companiesForLocation(locationId: number | null): TargetCompanyEntry[] {
    if (locationId == null) {
      return [];
    }
    const map = this.companyResults();
    return map[locationId] ?? [];
  }

  private loadLocations(serviceId: number): void {
    this.loading.set(true);
    this.errorMessage.set('');

    this.collectDataService
      .getTargetAreaSummary(serviceId)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (summaries) => {
          const allSummaries: TargetAreaSummary[] = summaries ?? [];
          const generatedSummaries = allSummaries.filter(
            (summary) => (summary.total_locations ?? 0) > 0
          );

          const audienceId = this.filteredAudienceId();

          const flattenedLocations: TargetAudienceLocation[] = [];

          generatedSummaries.forEach((summary) => {
            const rawAudienceId = Number(summary.audience?.id);
            if (!Number.isFinite(rawAudienceId) || rawAudienceId <= 0) {
              return;
            }

            this.uiState.markTargetAreaGenerated(serviceId, rawAudienceId);

            (summary.locations ?? []).forEach((location) => {
              const resolvedAudienceId = Number(
                location.target_audience_id ?? rawAudienceId
              );
              if (!Number.isFinite(resolvedAudienceId) || resolvedAudienceId <= 0) {
                return;
              }

              const resolvedLocation: TargetAudienceLocation = {
                ...location,
                target_audience_id: resolvedAudienceId,
                service_collected_id: serviceId,
                target_audience:
                  location.target_audience ??
                  this.toDisplayText(summary.audience?.target_audience) ??
                  null,
                audience_type:
                  location.audience_type ??
                  this.toDisplayText(summary.audience?.audience_type) ??
                  this.toDisplayText(summary.audience?.market_type) ??
                  null,
                market_type:
                  location.market_type ??
                  this.toDisplayText(summary.audience?.market_type) ??
                  null,
                industry:
                  location.industry ??
                  this.toDisplayText(summary.audience?.industries) ??
                  null,
                company_size:
                  location.company_size ??
                  this.toDisplayText(summary.audience?.company_size) ??
                  null
              };

              flattenedLocations.push(resolvedLocation);
            });
          });

          const filteredLocations =
            audienceId != null
              ? flattenedLocations.filter(
                  (location) => location.target_audience_id === audienceId
                )
              : flattenedLocations;

          this.locations.set(filteredLocations);
          this.errorMessage.set('');

          const totalLocations = filteredLocations.length;
          const audienceCount =
            audienceId != null ? (totalLocations ? 1 : 0) : generatedSummaries.length;

          this.statusMessage.set(
            totalLocations
              ? audienceId != null
                ? `Loaded ${totalLocations} target area record(s) for audience ${audienceId}.`
                : `Loaded ${totalLocations} target area record(s) across ${audienceCount} audience(s).`
              : audienceId != null
              ? 'No target areas are linked to the selected audience yet.'
              : 'No target areas are available yet.'
          );
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to load target areas. Please try again.';
          this.errorMessage.set(message);
          this.statusMessage.set('');
          this.locations.set([]);
        }
      });
  }

  private loadCompanies(serviceId: number, locationId: number): void {
    this.collectDataService
      .getTargetCompanies(serviceId, locationId)
      .subscribe({
        next: (response) => {
          const grouped = this.mapCompaniesByLocation(
            response.data ?? [],
            locationId
          );
          const next = { ...this.companyResults() };
          next[locationId] = grouped[locationId] ?? [];
          this.companyResults.set(next);
          if (!next[locationId]?.length) {
            this.companyStatus.set(
              'No companies are available for this location yet.'
            );
          }
          this.companyError.set('');
        },
        error: (error) => {
          const message =
            error?.error?.detail ||
            error?.message ||
            'Unable to load generated companies for this location.';
          this.companyError.set(message);
        }
      });
  }

  private mapCompaniesByLocation(
    companies: TargetCompany[],
    fallbackLocationId: number
  ): Record<number, TargetCompanyEntry[]> {
    const result: Record<number, TargetCompanyEntry[]> = {};
    companies.forEach((company) => {
      this.normaliseCompanyEntries(company.companies).forEach((entry) => {
        const key =
          this.coerceNumber(entry.location_id) ?? fallbackLocationId;
        if (key == null) {
          return;
        }
        if (!result[key]) {
          result[key] = [];
        }
        result[key].push(entry);
      });
    });
    return result;
  }

  private normaliseCompanyEntries(value: unknown): TargetCompanyEntry[] {
    const normalise = (entry: unknown): TargetCompanyEntry => {
      if (!entry || typeof entry !== 'object') {
        const name = this.coerceString(entry);
        return name ? { name } : {};
      }

      const record = entry as Record<string, unknown>;
      const rawLocation =
        record['location_id'] ?? record['locationId'] ?? record['LocationId'];
      const locationId = this.normaliseLocationIdentifier(rawLocation);

      return {
        location_id: locationId,
        name: this.coerceString(record['name']),
        industry: this.coerceString(record['industry']),
        location: this.coerceString(record['location']),
        size:
          this.coerceString(record['size']) ??
          this.coerceString(record['company_size']),
        website:
          this.coerceString(record['website']) ??
          this.coerceString(record['company_website'])
      };
    };

    if (Array.isArray(value)) {
      return value
        .map(normalise)
        .filter((entry) => (entry.name ?? '').trim().length);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return [];
      }
      if (
        (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
        (trimmed.startsWith('{') && trimmed.endsWith('}'))
      ) {
        try {
          const parsed = JSON.parse(trimmed);
          return this.normaliseCompanyEntries(parsed);
        } catch {
          return [{ name: trimmed }];
        }
      }

      return [{ name: trimmed }];
    }

    if (value && typeof value === 'object') {
      return [normalise(value)];
    }

    return [];
  }

  private coerceString(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '';
      }
    }
    return String(value);
  }

  private coerceNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parsed = Number(trimmed);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  private normaliseLocationIdentifier(value: unknown): number | string | null {
    const asNumber = this.coerceNumber(value);
    if (asNumber != null) {
      return asNumber;
    }
    const asString = this.coerceString(value);
    return asString ? asString : null;
  }

  private toDisplayText(value: unknown): string | null {
    if (value == null) {
      return null;
    }
    if (Array.isArray(value)) {
      const normalised = value
        .map((item) => this.toDisplayText(item))
        .filter(
          (item): item is string =>
            !!item && item.trim().length > 0
        );
      return normalised.length ? normalised.join(', ') : null;
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  }
}
